import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useKeycloakToken } from 'components/common/useKeycloak';
import { getRoutesByIntegration } from 'api/routes/routesApi';
import { ROUTE_SUBPAGES } from 'components/routes/routeSubpages';
import NotFoundPage from 'components/common/NotFoundPage';
import Spinner from 'components/common/Spinner';

// Old links never die, part 2 (see legacyPathRedirects.ts for the
// /destinations* family): before Routes and Connections were separate screens,
// a "connection" WAS what we now call a route, keyed by the PROVIDER
// integration id, and its details page had tabs at /connections/:id/<subpage>.
// The bare path shows the Connection itself now; this covers the
// tab deep links, which otherwise fall through the router into a 404.
//
// Only the suffixes the old screen actually had are carried over; anything
// else was never a page there, so it stays a 404. The new-vocabulary
// suffixes (/routes, /configuration) have their own explicit router entries,
// which outrank this :subpage param match.
export const LEGACY_CONNECTION_SUBPAGE_TARGETS: Record<
  string,
  (typeof ROUTE_SUBPAGES)[number]
> = {
  // Same tab, new home.
  logs: 'logs',
  sources: 'sources',
  // These tabs' content lives on the flow map now.
  overview: 'configurations',
  provider: 'configurations',
  destinations: 'configurations',
  webhook: 'configurations',
};

// Answer to "where does this URL go?", stamped with the params it was
// computed for so a param change can never reuse (or be overwritten by) a
// stale answer.
type ResolvedTarget = { key: string; target: string; replace: boolean };

const LegacyConnectionSubpageRedirect = () => {
  const { dest_id, subpage } = useParams();
  const location = useLocation();
  // useKeycloakToken (not raw useKeycloak) so an expired session surfaces the
  // app shell's LoginModal instead of stranding the user on this page.
  const [token] = useKeycloakToken();
  const { keycloak } = useKeycloak();
  const [answer, setAnswer] = useState<ResolvedTarget | null>(null);
  // Monotonic counter, same supersede pattern as ManageDestinationDetails'
  // provider-role lookup: only the latest request may answer.
  const requestId = useRef(0);

  // hasOwnProperty, not a bare index: /connections/:id/constructor must 404,
  // not resolve an Object.prototype member.
  const isLegacySubpage =
    !!subpage &&
    Object.prototype.hasOwnProperty.call(
      LEGACY_CONNECTION_SUBPAGE_TARGETS,
      subpage,
    );
  const mappedSubpage = isLegacySubpage
    ? LEGACY_CONNECTION_SUBPAGE_TARGETS[subpage as string]
    : undefined;
  // "new" is the creation flow's segment, never a legacy connection id.
  const isRealId = !!dest_id && dest_id !== 'new';
  // The search is part of the key because it is part of the answer (the
  // target embeds it) — a query-only change must invalidate both the cached
  // answer and any in-flight response.
  const paramsKey = `${dest_id}/${subpage}${location.search}`;

  useEffect(() => {
    if (!mappedSubpage || !isRealId || !dest_id) return;
    if (answer?.key === paramsKey) return; // this URL is already resolved
    if (!token || typeof token !== 'string' || keycloak.isTokenExpired())
      return;
    const request = ++requestId.current;
    const search = location.search; // preserved like legacyDestinationsTarget does
    const lookup = async () => {
      const data = await getRoutesByIntegration(token, 'provider', dest_id);
      // Drop a response a newer lookup has superseded.
      if (request !== requestId.current) return;
      // Route URLs carry the provider INTEGRATION id, which is the id this
      // old link already has — the lookup only answers whether there is a
      // Route page to send the user to at all.
      if (data.ok && data.results?.length) {
        setAnswer({
          key: paramsKey,
          target: `/routes/${dest_id}/${mappedSubpage}${search}`,
          replace: true,
        });
      } else {
        // No route resolved: the connection page still exists and links to
        // its routes, one click away — never strand the user on a 404.
        // A FAILED lookup is not a "no routes" answer (see
        // the version-switch resolver): fall back the same way, but without
        // replace, so Back can recover the original deep link for a retry.
        setAnswer({
          key: paramsKey,
          target: `/connections/${dest_id}${search}`,
          replace: data.ok,
        });
      }
    };
    lookup();
  }, [
    paramsKey,
    mappedSubpage,
    isRealId,
    dest_id,
    token,
    keycloak,
    answer,
    location.search,
  ]);

  if (!mappedSubpage || !isRealId) return <NotFoundPage />;
  // Lookup in flight (or waiting on a token refresh, during which the app
  // shell shows the login modal).
  if (!answer || answer.key !== paramsKey) return <Spinner />;
  return <Navigate to={answer.target} replace={answer.replace} />;
};

export default LegacyConnectionSubpageRedirect;
